/**
 * Consistency Debug Panel
 * 
 * A developer-visible panel that shows:
 * - Active report ID and client case ID
 * - Parsed values by section
 * - Whether each page is reading from the same saved parsed dataset
 * - Value source types (explicit, derived, workflow, fallback)
 * - Consistency validation errors
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useReportData, VALUE_SOURCE } from '../context/ReportDataContext';
import { canShowDebugTools } from '../utils/debugToolsAccess';
import {
  Shield,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Database,
  Eye,
  EyeOff,
  Copy,
  Bug
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const ConsistencyDebugPanel = () => {
  const location = useLocation();
  const { user } = useAuth();
  const {
    reportData,
    loading,
    error,
    hasReport,
    lastFetched,
    consistencyErrors,
    refreshData,
    bureauScores,
    averageScore
  } = useReportData();

  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [pageBindings, setPageBindings] = useState({});

  // Track which page we're on
  const currentPage = location.pathname;

  // Register this page as using the central data store
  useEffect(() => {
    if (hasReport) {
      setPageBindings(prev => ({
        ...prev,
        [currentPage]: {
          timestamp: new Date().toISOString(),
          reportId: reportData?.report_id,
          usingCentralStore: true
        }
      }));
    }
  }, [currentPage, hasReport, reportData?.report_id]);

  // Copy debug info to clipboard
  const copyDebugInfo = useCallback(() => {
    const debugInfo = {
      timestamp: new Date().toISOString(),
      currentPage,
      reportId: reportData?.report_id,
      hasReport,
      bureauScores,
      averageScore,
      consistencyErrors,
      lastFetched: lastFetched ? new Date(lastFetched).toISOString() : null,
      pageBindings
    };
    navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));
    toast.success('Debug info copied to clipboard');
  }, [reportData, hasReport, bureauScores, averageScore, consistencyErrors, lastFetched, pageBindings, currentPage]);

  // Never render for normal users (admin + dev + explicit flag only)
  if (!canShowDebugTools(user)) {
    return null;
  }

  // Don't render on auth pages
  if (currentPage.includes('/login') || currentPage.includes('/signup')) {
    return null;
  }

  // Mini toggle button when collapsed
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 p-2.5 bg-stone-800 hover:bg-slate-700 text-white rounded-lg shadow-lg transition-all"
        title="Open Data Consistency Panel"
        data-testid="consistency-debug-toggle"
      >
        <Bug size={20} />
      </button>
    );
  }

  // Source badge component
  const SourceBadge = ({ source }) => {
    const styles = {
      [VALUE_SOURCE.EXPLICIT]: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      [VALUE_SOURCE.DERIVED]: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      [VALUE_SOURCE.WORKFLOW]: 'bg-amber-100 text-amber-700 border-amber-200',
      [VALUE_SOURCE.NO_DATA]: 'bg-slate-100 text-slate-500 border-slate-200',
      [VALUE_SOURCE.FALLBACK]: 'bg-red-100 text-red-700 border-red-200',
    };

    return (
      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${styles[source] || styles[VALUE_SOURCE.NO_DATA]}`}>
        {source || 'unknown'}
      </span>
    );
  };

  return (
    <div 
      className="fixed bottom-4 right-4 z-50 w-[420px] max-h-[80vh] bg-stone-900 text-white rounded-xl shadow-2xl overflow-hidden flex flex-col"
      data-testid="consistency-debug-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-slate-700 bg-stone-800">
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-amber-400" />
          <span className="font-semibold text-sm">Data Consistency Panel</span>
          {consistencyErrors.length > 0 && (
            <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
              {consistencyErrors.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={copyDebugInfo}
            className="p-1.5 hover:bg-slate-700 rounded transition-colors"
            title="Copy debug info"
          >
            <Copy size={14} />
          </button>
          <button
            onClick={() => refreshData()}
            className="p-1.5 hover:bg-slate-700 rounded transition-colors"
            title="Refresh data"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-slate-700 rounded transition-colors"
          >
            <EyeOff size={14} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* Status Banner */}
        <div className={`p-2 rounded-lg flex items-center gap-2 ${
          error ? 'bg-red-900/50 border border-red-600' :
          !hasReport ? 'bg-amber-900/50 border border-amber-600' :
          consistencyErrors.length > 0 ? 'bg-red-900/50 border border-red-600' :
          'bg-emerald-900/50 border border-emerald-600'
        }`}>
          {error ? <XCircle size={16} className="text-red-400" /> :
           !hasReport ? <AlertTriangle size={16} className="text-amber-400" /> :
           consistencyErrors.length > 0 ? <AlertTriangle size={16} className="text-red-400" /> :
           <CheckCircle size={16} className="text-emerald-400" />}
          <span className="text-xs">
            {error ? `Error: ${error}` :
             !hasReport ? 'No report imported' :
             consistencyErrors.length > 0 ? `${consistencyErrors.length} consistency issue(s)` :
             'All data consistent'}
          </span>
        </div>

        {/* Section Tabs */}
        <div className="flex gap-1 text-[10px]">
          {['overview', 'bureaus', 'tradelines', 'workflow', 'pages'].map(section => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className={`px-2 py-1 rounded capitalize transition-colors ${
                activeSection === section 
                  ? 'bg-slate-700 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {section}
            </button>
          ))}
        </div>

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-stone-800 p-2 rounded">
                <p className="text-slate-400 text-[10px]">Report ID</p>
                <p className="font-mono text-[11px] truncate">{reportData?.report_id || 'None'}</p>
              </div>
              <div className="bg-stone-800 p-2 rounded">
                <p className="text-slate-400 text-[10px]">Last Fetched</p>
                <p className="text-[11px]">{lastFetched ? new Date(lastFetched).toLocaleTimeString() : 'Never'}</p>
              </div>
              <div className="bg-stone-800 p-2 rounded">
                <p className="text-slate-400 text-[10px]">Current Page</p>
                <p className="text-[11px] truncate">{currentPage}</p>
              </div>
              <div className="bg-stone-800 p-2 rounded">
                <p className="text-slate-400 text-[10px]">Data Source</p>
                <p className="text-[11px]">{hasReport ? 'Central Store' : 'None'}</p>
              </div>
            </div>

            {/* Quick Score Check */}
            {hasReport && bureauScores && (
              <div className="bg-stone-800 p-2 rounded">
                <p className="text-slate-400 text-[10px] mb-2">Bureau Scores (SINGLE SOURCE)</p>
                <div className="flex justify-between items-center">
                  <div className="text-center">
                    <p className="text-lg font-bold text-amber-400">{bureauScores.transunion ?? '--'}</p>
                    <p className="text-[9px] text-slate-400">TransUnion</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-emerald-400">{bureauScores.experian ?? '--'}</p>
                    <p className="text-[9px] text-slate-400">Experian</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-red-400">{bureauScores.equifax ?? '--'}</p>
                    <p className="text-[9px] text-slate-400">Equifax</p>
                  </div>
                  <div className="text-center border-l border-slate-600 pl-3">
                    <p className="text-lg font-bold text-slate-300">{averageScore ?? '--'}</p>
                    <p className="text-[9px] text-slate-400">Derived Avg</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bureaus Section */}
        {activeSection === 'bureaus' && hasReport && (
          <div className="space-y-2">
            {['transunion', 'experian', 'equifax'].map(bureau => {
              const data = reportData?.[bureau] || {};
              return (
                <div key={bureau} className="bg-stone-800 p-2 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium capitalize">{bureau}</p>
                    <SourceBadge source={data.score_source} />
                  </div>
                  <div className="grid grid-cols-3 gap-1 text-[10px]">
                    <div>
                      <p className="text-slate-400">Score</p>
                      <p className="font-semibold">{data.score ?? '--'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Total</p>
                      <p>{data.total_accounts ?? '--'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Open</p>
                      <p>{data.open_accounts ?? '--'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Closed</p>
                      <p>{data.closed_accounts ?? '--'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Derog</p>
                      <p className="text-red-400">{data.derogatory_count ?? '--'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Collections</p>
                      <p className="text-red-400">{data.collections_count ?? '--'}</p>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Derived totals */}
            <div className="bg-emerald-900/30 p-2 rounded border border-emerald-600/50">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium">Derived Totals</p>
                <SourceBadge source={VALUE_SOURCE.DERIVED} />
              </div>
              <div className="grid grid-cols-3 gap-1 text-[10px]">
                <div>
                  <p className="text-slate-400">Avg Score</p>
                  <p className="font-semibold">{reportData?.derived?.average_score ?? '--'}</p>
                </div>
                <div>
                  <p className="text-slate-400">Total Accts</p>
                  <p>{reportData?.derived?.total_accounts_combined ?? '--'}</p>
                </div>
                <div>
                  <p className="text-slate-400">Tradelines</p>
                  <p>{reportData?.derived?.tradeline_count ?? '--'}</p>
                </div>
              </div>
              <p className="text-[9px] text-slate-400 mt-1">Formula: {reportData?.derived?.average_score_formula}</p>
            </div>
          </div>
        )}

        {/* Tradelines Section */}
        {activeSection === 'tradelines' && hasReport && (
          <div className="space-y-2">
            <div className="bg-stone-800 p-2 rounded">
              <p className="text-xs font-medium mb-2">Tradeline Summary</p>
              <div className="grid grid-cols-3 gap-2 text-[10px]">
                <div className="text-center p-2 bg-slate-700 rounded">
                  <p className="text-lg font-bold">{reportData?.derived?.tradeline_count ?? 0}</p>
                  <p className="text-slate-400">Total</p>
                </div>
                <div className="text-center p-2 bg-red-900/30 rounded">
                  <p className="text-lg font-bold text-red-400">{reportData?.derived?.negative_tradeline_count ?? 0}</p>
                  <p className="text-slate-400">Negative</p>
                </div>
                <div className="text-center p-2 bg-emerald-900/30 rounded">
                  <p className="text-lg font-bold text-emerald-400">{reportData?.derived?.positive_tradeline_count ?? 0}</p>
                  <p className="text-slate-400">Positive</p>
                </div>
              </div>
            </div>

            {/* Sample tradelines */}
            <div className="bg-stone-800 p-2 rounded max-h-40 overflow-y-auto">
              <p className="text-[10px] text-slate-400 mb-1">First 5 tradelines (explicit):</p>
              {(reportData?.tradelines || []).slice(0, 5).map((t, i) => (
                <div key={i} className="flex items-center justify-between py-1 border-b border-slate-700 last:border-0">
                  <span className="text-[10px] truncate max-w-[200px]">{t.creditor_name}</span>
                  <div className="flex items-center gap-1">
                    {t.is_negative && <span className="text-[9px] text-red-400">NEG</span>}
                    <SourceBadge source={VALUE_SOURCE.EXPLICIT} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Workflow Section */}
        {activeSection === 'workflow' && hasReport && (
          <div className="space-y-2">
            <div className="bg-amber-900/30 p-2 rounded border border-amber-600/50">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium">Workflow State</p>
                <SourceBadge source={VALUE_SOURCE.WORKFLOW} />
              </div>
              <p className="text-[10px] text-slate-400 mb-2">
                Workflow values are SEPARATE from report-source truth
              </p>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div>
                  <p className="text-slate-400">Active Disputes</p>
                  <p className="font-semibold">{reportData?.workflow?.active_disputes ?? 0}</p>
                </div>
                <div>
                  <p className="text-slate-400">Completed</p>
                  <p>{reportData?.workflow?.completed_disputes ?? 0}</p>
                </div>
                <div>
                  <p className="text-slate-400">Draft</p>
                  <p>{reportData?.workflow?.disputes_by_status?.draft ?? 0}</p>
                </div>
                <div>
                  <p className="text-slate-400">Monitoring</p>
                  <p>{reportData?.workflow?.disputes_by_status?.monitoring ?? 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-stone-800 p-2 rounded text-[10px]">
              <p className="text-slate-400 mb-1">Disputed Tradeline IDs:</p>
              <p className="font-mono text-[9px]">
                {(reportData?.workflow?.disputed_tradeline_ids || []).join(', ') || 'None'}
              </p>
            </div>
          </div>
        )}

        {/* Pages Section */}
        {activeSection === 'pages' && (
          <div className="space-y-2">
            <div className="bg-stone-800 p-2 rounded">
              <p className="text-xs font-medium mb-2">Page Bindings</p>
              <p className="text-[10px] text-slate-400 mb-2">
                Pages should all read from the same central data store
              </p>
              {Object.entries(pageBindings).map(([page, info]) => (
                <div key={page} className="flex items-center justify-between py-1 border-b border-slate-700 last:border-0">
                  <span className="text-[10px] truncate max-w-[200px]">{page}</span>
                  <div className="flex items-center gap-1">
                    {info.usingCentralStore ? (
                      <CheckCircle size={12} className="text-emerald-400" />
                    ) : (
                      <XCircle size={12} className="text-red-400" />
                    )}
                    <span className="text-[9px] text-slate-400">
                      {new Date(info.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Consistency Errors */}
        {consistencyErrors.length > 0 && (
          <div className="bg-red-900/30 p-2 rounded border border-red-600/50">
            <p className="text-xs font-medium text-red-400 mb-2">Consistency Errors</p>
            {consistencyErrors.map((err, i) => (
              <div key={i} className="text-[10px] py-1 border-b border-red-800/50 last:border-0">
                <p className="font-medium">{err.field}</p>
                <p className="text-slate-400">{err.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* Parsing Metadata */}
        {hasReport && reportData?.parsing_metadata && (
          <div className="bg-stone-800 p-2 rounded text-[10px]">
            <p className="text-slate-400 mb-1">Parsing Info</p>
            <div className="space-y-0.5">
              <p>Provider: <span className="text-white">{reportData.parsing_metadata.provider_detected}</span></p>
              <p>Schema: <span className="text-white">{reportData.parsing_metadata.schema_version}</span></p>
              <p>Demo: <span className={reportData.parsing_metadata.is_demo_data ? 'text-red-400' : 'text-emerald-400'}>
                {reportData.parsing_metadata.is_demo_data ? 'Yes' : 'No'}
              </span></p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-2 border-t border-slate-700 bg-stone-800 text-[10px] text-slate-400 flex items-center justify-between">
        <span>Single Source of Truth: {hasReport ? 'Active' : 'N/A'}</span>
        <div className="flex items-center gap-1">
          <div className={`w-2 h-2 rounded-full ${hasReport ? 'bg-emerald-500' : 'bg-slate-500'} animate-pulse`}></div>
          <span>{loading ? 'Loading...' : 'Ready'}</span>
        </div>
      </div>
    </div>
  );
};

export default ConsistencyDebugPanel;
